/**
 * Auth Service
 * Business logic layer for authentication operations
 */

import { axiosInstance } from '../api/axios.config';
import { AUTH_ENDPOINTS, STORAGE_KEYS } from '../constants';
import type {
  LoginCredentials,
  AuthResponse,
  ApiAuthResponse,
  User,
  CreateClinicData,
  CreateClinicAdminData,
  RefreshTokenResponse,
} from '../types/auth.types';
import type { ClinicRegistrationData } from '../types/clinic.types';

/**
 * Auth Service
 * Handles authentication business logic and token management
 */
export class AuthService {
  /**
   * Login user and store tokens
   */
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<ApiAuthResponse>(
        AUTH_ENDPOINTS.LOGIN,
        credentials
      );
      
      // Validate successful response
      if (!response.data.success) {
        throw new Error('Login failed');
      }
      
      // Map API response to internal format
      // Handle both _id (MongoDB) and id field names
      const userId = response.data.user._id || response.data.user.id;
      if (!userId) {
        throw new Error('User ID not found in response');
      }
      
      // Validate token
      const token = response.data.token || response.data.accessToken;
      if (!token) {
        throw new Error('Authentication token not found in response');
      }
      
      const authResponse: AuthResponse = {
        user: {
          id: userId,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          email: response.data.user.email,
          role: response.data.user.role,
          isActive: response.data.user.isActive,
          // Map optional fields
          clinicId: response.data.user.clinicId,
          staffId: response.data.user.staffId,
          phone: response.data.user.phone,
          avatar: response.data.user.avatar,
          profileCompleted: response.data.user.profileCompleted,
          createdAt: response.data.user.createdAt,
          updatedAt: response.data.user.updatedAt,
          credentials: (response.data.user as any)?.credentials,
        },
        token: token,
        refreshToken: response.data.refreshToken || '',
      };
      
      // Store tokens
      this.setTokens(authResponse.token, authResponse.refreshToken);
      
      // Store user data
      this.setUser(authResponse.user);
      
      return authResponse;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout user and clear storage
   */
  static async logout(): Promise<void> {
    try {
      await axiosInstance.post(AUTH_ENDPOINTS.LOGOUT);
    } finally {
      // Always clear local storage even if API call fails
      this.clearAuth();
    }
  }

  /**
   * Refresh access token
   */
  static async refreshAccessToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axiosInstance.post<ApiAuthResponse | RefreshTokenResponse>(
      AUTH_ENDPOINTS.REFRESH_TOKEN,
      { refreshToken }
    );
    
    // Handle both response formats
    const accessToken = 'accessToken' in response.data 
      ? response.data.accessToken 
      : response.data.token;
    const newRefreshToken = response.data.refreshToken;
    
    // Validate tokens
    if (!accessToken) {
      throw new Error('Access token not found in refresh response');
    }
    if (!newRefreshToken) {
      throw new Error('Refresh token not found in refresh response');
    }
    
    // Update tokens
    this.setTokens(accessToken, newRefreshToken);
    
    return accessToken;
  }

  /**
   * Request password reset
   */
  static async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await axiosInstance.post<{ message: string }>(
      AUTH_ENDPOINTS.FORGOT_PASSWORD,
      { email }
    );
    return response.data;
  }

  /**
   * Reset password (authenticated)
   */
  static async resetPassword(newPassword: string): Promise<{ message: string }> {
    const response = await axiosInstance.put<{ message: string }>(
      AUTH_ENDPOINTS.RESET_PASSWORD,
      { newPassword }
    );
    return response.data;
  }

  /**
   * Create new clinic (public endpoint)
   * Accepts either CreateClinicData or ClinicRegistrationData
   */
  static async createClinic(data: CreateClinicData | ClinicRegistrationData): Promise<AuthResponse & { clinic?: any }> {
    try {
      const response = await axiosInstance.post<ApiAuthResponse & { clinic?: any; message?: string }>(
        AUTH_ENDPOINTS.CREATE_CLINIC,
        data
      );
      
      // Check if this is a pending approval response (no user/token returned)
      // API returns: { message: "Clinic created successfully", clinic: {...} }
      if (response.data.message && !response.data.user && !response.data.accessToken) {
        // Clinic created but pending admin approval
        // Return a minimal response with clinic data only
        return {
          user: null as any, // Will be populated after approval
          token: '',
          refreshToken: '',
          clinic: response.data.clinic,
          message: response.data.message,
        } as AuthResponse & { clinic?: any; message?: string };
      }
      
      // Map API response to internal format (for approved clinic registration)
      // Handle both _id (MongoDB) and id field names
      const userId = response.data.user._id || response.data.user.id;
      if (!userId) {
        throw new Error('User ID not found in response');
      }
      
      // Validate token
      const token = response.data.token || response.data.accessToken;
      if (!token) {
        throw new Error('Authentication token not found in response');
      }
      
      const authResponse: AuthResponse = {
        user: {
          id: userId,
          email: response.data.user.email,
          role: response.data.user.role,
          clinicId: response.data.user.clinicId,
          ...(response.data.user.staffId && { staffId: response.data.user.staffId }),
          ...(response.data.user.firstName && { firstName: response.data.user.firstName }),
          ...(response.data.user.lastName && { lastName: response.data.user.lastName }),
          ...(response.data.user.phone && { phone: response.data.user.phone }),
          ...(response.data.user.avatar && { avatar: response.data.user.avatar }),
          ...(response.data.user.isActive !== undefined && { isActive: response.data.user.isActive }),
          ...(response.data.user.createdAt && { createdAt: response.data.user.createdAt }),
          ...(response.data.user.updatedAt && { updatedAt: response.data.user.updatedAt }),
        },
        token: token,
        refreshToken: response.data.refreshToken || '',
      };
      
      // Store tokens
      if (authResponse.token) {
        this.setTokens(authResponse.token, authResponse.refreshToken);
      }
      
      // Store user data
      if (authResponse.user) {
        this.setUser(authResponse.user);
      }
      
      return { ...authResponse, clinic: response.data.clinic };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create clinic admin (public endpoint)
   */
  static async createClinicAdmin(data: CreateClinicAdminData): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<ApiAuthResponse>(
        AUTH_ENDPOINTS.CREATE_CLINIC_ADMIN,
        data
      );
      
      // Map API response to internal format
      // Handle both _id (MongoDB) and id field names
      const userId = response.data.user._id || response.data.user.id;
      if (!userId) {
        throw new Error('User ID not found in response');
      }
      
      // Validate token
      const token = response.data.token || response.data.accessToken;
      if (!token) {
        throw new Error('Authentication token not found in response');
      }
      
      const authResponse: AuthResponse = {
        user: {
          id: userId,
          email: response.data.user.email,
          role: response.data.user.role,
          clinicId: response.data.user.clinicId,
          ...(response.data.user.staffId && { staffId: response.data.user.staffId }),
          ...(response.data.user.firstName && { firstName: response.data.user.firstName }),
          ...(response.data.user.lastName && { lastName: response.data.user.lastName }),
          ...(response.data.user.phone && { phone: response.data.user.phone }),
          ...(response.data.user.avatar && { avatar: response.data.user.avatar }),
          ...(response.data.user.isActive !== undefined && { isActive: response.data.user.isActive }),
          ...(response.data.user.createdAt && { createdAt: response.data.user.createdAt }),
          ...(response.data.user.updatedAt && { updatedAt: response.data.user.updatedAt }),
        },
        token: token,
        refreshToken: response.data.refreshToken || '',
      };
      
      // Store tokens
      this.setTokens(authResponse.token, authResponse.refreshToken);
      
      // Store user data
      this.setUser(authResponse.user);
      
      return authResponse;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify current token
   */
  static async verifyToken(): Promise<User | null> {
    try {
      const token = this.getToken();
      if (!token) return null;

      // Use /auth/me for verification since it is canonical and consistent across envs.
      // This also refreshes the stored user profile.
      const user = await this.getCurrentUser();
      return user;
    } catch (error) {
      // Don't eagerly wipe auth on verify failures; let 401 handling / explicit logout do that.
      // This prevents temp-password flows from breaking when verify endpoints change.
      console.error('AuthService.verifyToken failed:', error);
      return null;
    }
  }

  /**
   * Get current authenticated user profile
   * Fetches complete user data from /api/v1/auth/me
   */
  static async getCurrentUser(): Promise<User> {
    const response = await axiosInstance.get<{ success: boolean; data: any }>(
      AUTH_ENDPOINTS.ME
    );

    if (!response.data.success) {
      throw new Error('Failed to fetch user profile');
    }

    const userData = response.data.data;

    // Map API response to User type
    const user: User = {
      id: userData._id || userData.id,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      fullName: userData.fullName,
      role: userData.role,
      phone: userData.phone,
      avatar: userData.avatar,
      isActive: userData.isActive,
      isEmailVerified: userData.isEmailVerified,
      isLocked: userData.isLocked,
      clinicId: userData.clinicId,
      staffId: userData.staffId,
      agency: userData.agency,
      permissions: userData.permissions || [],
      twoFactorEnabled: userData.twoFactorEnabled,
      loginAttempts: userData.loginAttempts,
      timezone: userData.timezone,
      language: userData.language,
      notificationPreferences: userData.notificationPreferences,
      profileCompleted: userData.profileCompleted,
      lastLogin: userData.lastLogin,
      createdAt: userData.createdAt,
      updatedAt: userData.updatedAt,
    };

    // Store user data
    this.setUser(user);

    return user;
  }

  /**
   * Change password for authenticated user
   */
  static async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await axiosInstance.post<{ message: string }>(
      AUTH_ENDPOINTS.CHANGE_PASSWORD,
      { currentPassword, newPassword }
    );
    return response.data;
  }

  // Token Management

  /**
   * Get access token from storage
   */
  static getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Get refresh token from storage
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Set tokens in storage
   */
  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  /**
   * Get user from storage
   */
  static getUser(): User | null {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  }

  /**
   * Set user in storage
   */
  static setUser(user: User): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  /**
   * Clear all auth data from storage
   */
  static clearAuth(): void {
    // Clear all local storage data on logout
    localStorage.clear();
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export default AuthService;
